import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { ownerTypeNoun, type CommunicationChannel } from "@/api/communications";
import {
  useCommunicationsByOwner,
  useCommunicationsRecentCount,
} from "../hooks/useCommunications";
import { ChannelFilterChips } from "./ChannelFilterChips";
import { CommunicationListRow } from "./CommunicationListRow";
import { LogCommunicationModal } from "./LogCommunicationModal";

interface Props {
  ownerType: string;
  ownerId: string;
  /** Defaults to 30 days. Surfaces in the recent-count badge above the list. */
  recentBadgeWindowDays?: number;
  /** Optional pre-formatted label for the locked placement chip in the modal. */
  ownerLabel?: string;
}

const URL_PARAM = "commTabChannel";

/**
 * Comm Log Standard §9.3 — the "Communications" tab content for any
 * in-scope entity detail page. Owns:
 *  - A "+ Log communication" button that opens
 *    <LogCommunicationModal> with the launching entity prefilled and
 *    LOCKED in the placement list (the user can ADD other placements
 *    but cannot remove this one).
 *  - A row of channel filter chips persisted via useSearchParams under
 *    the param key <c>commTabChannel</c> so deep-linked tabs round-trip.
 *  - The by-owner feed paginated 50/page.
 *
 * Built here for PR 5+ to embed directly. Not yet wired onto entity
 * detail pages in this PR.
 */
export function CommunicationsTabPanel({
  ownerType,
  ownerId,
  recentBadgeWindowDays = 30,
  ownerLabel,
}: Props) {
  const { hasPermission } = useAuth();
  const canLog = hasPermission("Communications.Log");
  const [params, setParams] = useSearchParams();
  const channel =
    (params.get(URL_PARAM) as CommunicationChannel | null) ?? null;
  const [openModal, setOpenModal] = useState(false);

  const list = useCommunicationsByOwner(ownerType, ownerId);
  const recentCountQ = useCommunicationsRecentCount(
    ownerType,
    ownerId,
    recentBadgeWindowDays,
  );

  const visibleEntries = (list.data ?? []).filter(
    (e) => !channel || e.channel === channel,
  );

  const onChannelChange = (next: CommunicationChannel | null) => {
    const nextParams = new URLSearchParams(params);
    if (next === null) nextParams.delete(URL_PARAM);
    else nextParams.set(URL_PARAM, next);
    setParams(nextParams, { replace: true });
  };

  const recentCount = recentCountQ.data ?? 0;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-space-3 mb-space-3">
        <div>
          <h2 className="font-display font-bold text-h3 text-onyx m-0">
            Communications
            {recentCount > 0 && (
              <span className="ml-space-2 text-label text-gray-mid">
                ({recentCount} in last {recentBadgeWindowDays} days)
              </span>
            )}
          </h2>
        </div>
        {canLog && (
          <Button onClick={() => setOpenModal(true)} type="button">
            <Plus size={14} aria-hidden /> Log communication
          </Button>
        )}
      </div>

      <ChannelFilterChips
        value={channel}
        onChange={onChannelChange}
        className="mb-space-3"
      />

      {list.isLoading && (
        <p className="text-small text-gray-mid">Loading communications…</p>
      )}
      {list.isError && (
        <p className="text-small text-status-error">
          Could not load communications. {(list.error as Error).message}
        </p>
      )}
      {list.data && visibleEntries.length === 0 && (
        <div className="rounded-md border border-border-light bg-white-pure p-space-4 text-center">
          {/* `ownerTypeNoun`, not `ownerTypeLabel(...).toLowerCase()` — the
              latter turned "SPV" into "this spv yet" and "TPS" into "this tps
              yet" on three of the surfaces this panel serves. */}
          <p className="text-small text-gray-mid m-0">
            No communications logged on this {ownerTypeNoun(ownerType)} yet.
            Log the first call from any record interaction.
          </p>
        </div>
      )}
      {visibleEntries.length > 0 && (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {visibleEntries.map((entry) => (
            <li key={entry.id}>
              <CommunicationListRow entry={entry} />
            </li>
          ))}
        </ul>
      )}

      <LogCommunicationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        preFilledOwner={{ type: ownerType, id: ownerId, label: ownerLabel }}
      />
    </div>
  );
}
